/**
 * usePushNotifications — Hook para suscribirse/desuscribirse a Web Push.
 * Gestiona el ciclo: registro SW → solicitar permiso → suscribirse → guardar en backend.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useUserStore } from '@/store/userStore';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const { token } = useUserStore();
  const [supported, setSupported]   = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading]       = useState(false);

  useEffect(() => {
    const ok = 'serviceWorker' in navigator && 'PushManager' in window;
    setSupported(ok);
    if (ok) {
      setPermission(Notification.permission);
      checkSubscriptionStatus();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkSubscriptionStatus = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_BASE}/api/push/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setSubscribed(d.subscribed);
      }
    } catch { /* ignore */ }
  }, [token]);

  const subscribe = useCallback(async () => {
    if (!supported || !token) return;
    setLoading(true);
    try {
      // 1. Register service worker
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // 2. Request notification permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        toast.error('Permiso de notificaciones denegado');
        setLoading(false);
        return;
      }

      // 3. Get VAPID public key
      const vResp = await fetch(`${API_BASE}/api/push/vapid-public-key`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!vResp.ok) throw new Error('No se pudo obtener la clave VAPID');
      const { public_key } = await vResp.json();

      // 4. Subscribe via PushManager
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });

      // 5. Send subscription to backend
      const resp = await fetch(`${API_BASE}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscription: subscription.toJSON() }),
      });
      if (!resp.ok) throw new Error('No se pudo guardar la suscripción');
      setSubscribed(true);
      toast.success('Notificaciones activadas — te avisaremos cuando haya nuevos Near Misses');
    } catch (err) {
      toast.error(`Error activando notificaciones: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [supported, token]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await fetch(`${API_BASE}/api/push/unsubscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ endpoint: sub.endpoint }),
          });
          await sub.unsubscribe();
        }
      }
      setSubscribed(false);
      toast.success('Notificaciones desactivadas');
    } catch (err) {
      toast.error(`Error desactivando: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe };
}
